import { Middleware, AnyAction } from '@reduxjs/toolkit';
import { getMapOfSpeakerLayout, buildRequestFromReduxState } from 'services/hearSpaceService';
import { MiddlewareActionMeta, PlotDataMeta } from 'types/middleware/actionMeta';
import { HEARSPACE_CONFIG, isHearSpaceEnabled, logHearSpace } from 'config/hearSpace';
import { getCacheKey, getCachedResponse, setCachedResponse, clearCache } from 'services/hearSpaceCache';
import { GetMapOfSpeakerLayoutResponse } from 'types/services/hearSpace';
import { hearSpaceRequestManager } from 'services/hearSpaceRequestManager';
import { mapResponseToPlotData, applyBarSpeakerScaling } from 'services/hearSpaceDataMapper';
import {
  recordTrigger,
  recordCacheHit,
  recordCacheMiss,
  recordApiCall,
  recordApiSuccess,
  recordApiFailure,
  recordDeduplicated,
  recordSkippedMaxWait,
  resetHearSpaceMetrics as resetMetrics,
  getHearSpaceMetrics,
} from 'services/hearSpaceMetrics';

// Re-export so callers don't need to import from two places
export { logRequestHistory } from 'services/hearSpaceMetrics';

interface Store {
  getState: () => any;
  dispatch: (action: any) => void;
}

/**
 * @module hearSpaceMiddleware
 * @description Orchestration middleware for the HearSpace Acoustics Engine API.
 *
 * Responsibilities (orchestration only — pure logic lives in extracted services):
 * - Decide when to trigger a HearSpace calculation (shouldCallHearSpace)
 * - Debounce rapid state changes with a max-wait ceiling
 * - Fan out calculations across all areas in parallel (callHearSpaceAPI)
 * - Dispatch plotData updates back into Redux state
 *
 * Pure logic delegated to:
 * - hearSpaceCache.ts     — response caching (5-min TTL, key generation)
 * - hearSpaceRequestManager.ts — in-flight deduplication
 * - hearSpaceDataMapper.ts    — response → plotData conversion, bar scaling
 * - hearSpaceMetrics.ts       — counters and request log
 */

/**
 * Actions that should trigger HearSpace calculation.
 * NOTE: View changes (ui/setCurrentView) removed — acoustics view just displays
 * existing plotData calculated in the background when actual data changed.
 */
const HEARSPACE_TRIGGERS = new Set([
  'container/setDimensions',
  'container/setCeilingHeight',
  'container/setSpeakers',
  'container/setSpeakerModel',
  'container/setSpeakerMountingType',
  'container/updateDevice',
  'container/addDevice',
  'container/moveDevice',
  'container/removeDevice',
  'container/deleteSpeaker',
  'container/updateSpeakerTarget',
  'container/updateSpeakerPosition',
  'container/toggleSpeakerLink',
  'container/resetArea',
  'container/setTable',
  'container/setExpansionSpeakerLayout',
  // Removal (type → 'none') nulls area.expansionSpeakers, so the recalc returns
  // expansionSpeakers: null and never dispatches setExpansionSpeakerLayout. Without this
  // trigger HearSpace never re-runs, leaving the stale bar+expansion SPL heatmap on screen.
  // On this action area.speakers still holds the bar, so HearSpace recomputes a bar-only map.
  'container/setExpansionSpeakerType',
]);

const MAX_WAIT = 3000; // ms — maximum debounce ceiling

// Device-manipulation actions: only trigger HearSpace when they affect speakers.
// Module-level constant — avoids allocating a new Set on every Redux dispatch.
const DEVICE_ACTIONS = new Set([
  'container/updateDevice',
  'container/moveDevice',
  'container/addDevice',
  'container/removeDevice',
]);

// ---------------------------------------------------------------------------
// Module-level debounce state (reset on new design load)
// ---------------------------------------------------------------------------
let hearSpaceTimer: NodeJS.Timeout | null = null;
let firstTriggerTime: number | null = null;
let initialCalculationDone = false;

// ---------------------------------------------------------------------------
// Trigger detection
// ---------------------------------------------------------------------------

const shouldCallHearSpace = (action: AnyAction, state: any): boolean => {
  // Loop prevention: skip actions dispatched by HearSpace itself —
  // EXCEPT when _triggerHearSpace is set (explicit signal from calculateDevicesMiddleware
  // after populating an empty CBC bar layout).
  if (
    action.type === 'container/updateDevice' &&
    !action.payload?.speakers?._triggerHearSpace &&
    (action.payload?._fromSyncMiddleware ||
      action.payload?.speakers?.plotData?._fromHearSpace ||
      action.payload?.properties?.plotData?._fromHearSpace)
  ) {
    return false;
  }

  const currentTool = state.container?.currentTool;
  if (!currentTool || !isHearSpaceEnabled(currentTool)) {
    return false;
  }

  const isRelevantAction = HEARSPACE_TRIGGERS.has(action.type);

  // Skip toggleSpeakerLink when unlinking — positions unchanged, plot stays valid
  if (action.type === 'container/toggleSpeakerLink' && isRelevantAction) {
    const area = state.container?.areas?.[state.container?.activeTab];
    if (!area?.speakers?.linkedSpeakers) {
      return false;
    }
  }

  // For device-manipulation actions only trigger when the update affects speakers
  if (DEVICE_ACTIONS.has(action.type) && isRelevantAction) {
    const isSpeakerUpdate =
      action.payload?.device === 'speakers' ||
      action.payload?.speakers !== undefined;
    if (!isSpeakerUpdate) return false;

    // Skip mid-drag moveDevice — only recalculate on pointer-up (final position)
    if (action.type === 'container/moveDevice' && state.ui?.deviceIsMoving) {
      return false;
    }
  }

  // Retrigger when switching to speakers view without confirmed HearSpace data
  if (action.type === 'ui/setCurrentView' && action.payload === 'speakers') {
    const area = state.container?.areas?.[state.container?.activeTab];
    const hasSpeakers = area?.speakers?.layout?.length > 0;
    const hasHearSpacePlot = area?.speakers?.plotData?._fromHearSpace;
    return hasSpeakers && !hasHearSpacePlot;
  }

  return isRelevantAction;
};

const shouldPerformInitialCalculation = (action: AnyAction, state: any): boolean => {
  if (initialCalculationDone) return false;

  const currentTool = state.container?.currentTool;
  if (!currentTool || !isHearSpaceEnabled(currentTool)) return false;

  const areas = state.container?.areas;
  if (!areas) return false;

  const hasSpeakers = Object.values(areas).some(
    (area: any) => area?.speakers?.layout?.length > 0,
  );
  if (!hasSpeakers) return false;

  return (
    action.type.includes('REHYDRATE') ||
    action.type.includes('@@INIT') ||
    action.type.includes('loadDesign') ||
    action.type.includes('setAreas') ||
    action.type.includes('setMicrophones') ||
    action.type.includes('setSpeakers') ||
    action.type === '@@redux/INIT' ||
    (action.type.startsWith('container/') && Object.keys(areas).length > 0)
  );
};

// ---------------------------------------------------------------------------
// API call — single area
// ---------------------------------------------------------------------------

const callHearSpaceAPIForArea = async (
  areaId: string,
  dispatch: any,
  getState: any,
  action?: any,
): Promise<void> => {
  const state = getState();
  if (!state.container?.areas?.[areaId]) return;

  let area = state.container.areas[areaId];

  // Merge fresh speaker data from the triggering action so we calculate with
  // the NEW positions, not the stale state snapshot (critical for updateDevice).
  if (action?.type === 'container/updateDevice' && action?.payload) {
    const hasDeviceData = Object.keys(action.payload).some(
      (key) =>
        ['speakers', 'microphones', 'cameras'].includes(key) &&
        action.payload[key]?.layout,
    );
    if (hasDeviceData) {
      area = { ...area, ...action.payload };
    }
  }

  if (!area.speakers?.layout?.length) return;

  // combined coverage in the Speakers view. Merge the expansion
  // speaker layout (`area.expansionSpeakers.layout`, set by `calcExpansionSpeakerPlacement`
  // in `roomCalculations.ts`) into the speakers payload sent to HearSpace so the returned
  // SPL plot reflects both the bar speaker AND the expansion speakers. No-op when no
  // expansion speakers are configured. The expansion entries already have the same shape
  // (model, modelName, location.{x,y,z}) as bar entries, so `convertToHearSpaceSpeakers`
  // accepts them without modification. Cache key naturally invalidates when the merged
  // layout changes because it's derived from the full request.
  // Expansion speakers are sent to HearSpace with a per-speaker TotalGain attenuation so
  // they don't inflate the room-wide mean SPL (DirectSplAwtd) that the Direct SPL heatmap
  // legend auto-centers on. Without this, distant expansion speakers rescale the bar's
  // apparent coverage. `convertToHearSpaceSpeakers` maps `gain` → `TotalGain`; the bar
  // speaker keeps no gain. Value is configurable via HEARSPACE_CONFIG.expansionSpeakerTotalGain.
  const expansionSpeakerTotalGain = HEARSPACE_CONFIG.expansionSpeakerTotalGain ?? -16;
  const expansionSpeakerLayout = area.expansionSpeakers?.layout ?? [];
  const combinedSpeakers = expansionSpeakerLayout.length > 0
    ? {
        ...area.speakers,
        layout: [
          ...area.speakers.layout,
          ...expansionSpeakerLayout.map((s: any) => ({ ...s, gain: s.gain ?? expansionSpeakerTotalGain })),
        ],
      }
    : area.speakers;

  const roomData = {
    dimensions: area.dimensions,
    ceilingHeight: area.ceilingHeight,
    speakers: combinedSpeakers,
    acoustics: area.acoustics,
  };

  // Compute receiver spacing that covers the full room without gaps
  const { length: roomLength, width: roomWidth } = area.dimensions;
  const baseSpacing = HEARSPACE_CONFIG.defaultReceiverSpacing;
  const receiversX = Math.ceil(roomLength / baseSpacing) + 1;
  const receiversY = Math.ceil(roomWidth / baseSpacing) + 1;
  const optimalSpacing = Math.min(
    roomLength / (receiversX - 1),
    roomWidth / (receiversY - 1),
  );

  const request = buildRequestFromReduxState(roomData, {
    receiverSpacing: optimalSpacing,
    includeSpl: true,
    directSoundOnly: HEARSPACE_CONFIG.directSoundOnly,
    noiseLevel: HEARSPACE_CONFIG.defaultNoiseLevel,
  });

  const isBarSpeaker = area.speakers.model === 'barspkr';

  try {
    const cacheKey = getCacheKey(request);
    const cached = getCachedResponse(cacheKey);

    let response: GetMapOfSpeakerLayoutResponse;

    if (cached) {
      response = cached;
      recordCacheHit(request, response);
      logHearSpace('✓ Using cached response');
    } else {
      recordCacheMiss();
      if (hearSpaceRequestManager.isInFlight(cacheKey)) {
        recordDeduplicated();
        logHearSpace(`⏳ Skipping duplicate in-flight request for area ${areaId}`);
        return;
      }

      hearSpaceRequestManager.add(cacheKey);
      try {
        logHearSpace('Starting acoustic calculation...', {
          dimensions: `${request.DimensionX}x${request.DimensionY}x${request.DimensionZ}m`,
          speakers: request.Speakers.length,
        });

        recordApiCall();
        const startTime = Date.now();
        response = await getMapOfSpeakerLayout(request);
        const responseTime = Date.now() - startTime;

        setCachedResponse(cacheKey, response);
        recordApiSuccess(request, response, responseTime);
        logHearSpace(`✓ API call completed in ${responseTime}ms`);
      } finally {
        hearSpaceRequestManager.remove(cacheKey);
      }
    }

    const plotData = mapResponseToPlotData(response, areaId);
    if (!plotData) return;

    const processedPlotData = isBarSpeaker
      ? applyBarSpeakerScaling(plotData)
      : plotData;

    const plotMeta: PlotDataMeta = { _fromHearSpace: true };
    const markedPlotData = { ...processedPlotData, ...plotMeta };

    const currentActiveTab = getState().container?.activeTab;

    if (currentActiveTab !== areaId) {
      dispatch({ type: 'container/setActiveTab', payload: areaId });
    }

    const hearSpaceMeta: MiddlewareActionMeta = { _fromSyncMiddleware: true };
    dispatch({
      type: 'container/updateDevice',
      payload: { speakers: { plotData: markedPlotData }, ...hearSpaceMeta },
    });

    if (currentActiveTab !== areaId && currentActiveTab) {
      dispatch({ type: 'container/setActiveTab', payload: currentActiveTab });
    }

    logHearSpace(`Updated speaker plot data for area: ${areaId}`);
  } catch (error) {
    recordApiFailure(request, error instanceof Error ? error.message : String(error));
    logHearSpace(`Failed for area ${areaId}:`, error instanceof Error ? error.message : String(error));
    throw error; // let Promise.allSettled in callHearSpaceAPI see the rejection
  }
};

// ---------------------------------------------------------------------------
// API call — all areas (wrapper)
// ---------------------------------------------------------------------------

const callHearSpaceAPI = async (
  dispatch: any,
  getState: any,
  action?: any,
): Promise<void> => {
  const areas = getState().container?.areas;
  if (!areas) return;

  const areaIds = Object.keys(areas).filter(
    (id) => areas[id]?.speakers?.layout?.length > 0,
  );
  if (!areaIds.length) return;

  dispatch({ type: 'ui/setHearSpaceLoading', payload: true });

  try {
    const results = await Promise.allSettled(
      areaIds.map((id) => callHearSpaceAPIForArea(id, dispatch, getState, action)),
    );

    if (results.some((r) => r.status === 'rejected')) {
      dispatch({
        type: 'ui/setToast',
        payload: {
          message: 'Some acoustic calculations failed. Using partial results.',
          severity: 'warning',
        },
      });
    }
  } finally {
    dispatch({ type: 'ui/setHearSpaceLoading', payload: false });
  }
};

// ---------------------------------------------------------------------------
// Main middleware
// ---------------------------------------------------------------------------

const hearSpaceMiddleware: Middleware = ({ dispatch, getState }: Store) => next => (action: AnyAction) => {
  // Reset per-design state when a new design is loaded
  if (action.type === 'container/setProjectData') {
    initialCalculationDone = false;
    firstTriggerTime = null;
    if (hearSpaceTimer) {
      clearTimeout(hearSpaceTimer);
      hearSpaceTimer = null;
    }
    logHearSpace('New design loaded — resetting state');
  }

  const result = next(action);
  const state = getState();

  // Initial calculation for designs that already have speakers when loaded
  if (shouldPerformInitialCalculation(action, state)) {
    initialCalculationDone = true;
    logHearSpace('Design loaded with speakers — triggering initial calculation');
    setTimeout(() => callHearSpaceAPI(dispatch, getState), 100);
  }

  if (shouldCallHearSpace(action, state)) {
    // Undo is a single discrete click, not a rapid stream of updates like a drag —
    // debouncing it just delays "Calculating..." from appearing (and the fresh heatmap
    // from arriving) by up to HEARSPACE_CONFIG.debounceTime for no benefit. Recalculate
    // immediately so the loading overlay shows right when the user clicks Undo, matching
    // the immediate `speakers.plotData` clear in room.ts/logic.js's resetArea handling.
    const shouldBypassDebounce =
      action.payload?._forceSpeakerRecalc ||
      action.payload?.speakers?._forceSpeakerRecalc ||
      action.type === 'container/resetArea';

    if (shouldBypassDebounce) {
      logHearSpace('Force recalculation — bypassing debounce');
      recordTrigger();
      if (hearSpaceTimer) { clearTimeout(hearSpaceTimer); hearSpaceTimer = null; }
      callHearSpaceAPI(dispatch, getState, action);
      firstTriggerTime = null;
    } else {
      recordTrigger();

      const now = Date.now();
      if (!firstTriggerTime) firstTriggerTime = now;

      if (hearSpaceTimer) clearTimeout(hearSpaceTimer);

      const elapsed = now - firstTriggerTime;
      let timeUntilCall: number;

      if (elapsed >= MAX_WAIT) {
        timeUntilCall = 0;
        recordSkippedMaxWait();
        logHearSpace('Max wait exceeded — calling immediately');
      } else {
        timeUntilCall = Math.min(HEARSPACE_CONFIG.debounceTime, MAX_WAIT - elapsed);
      }

      hearSpaceTimer = setTimeout(() => {
        callHearSpaceAPI(dispatch, getState);
        hearSpaceTimer = null;
        firstTriggerTime = null;
      }, timeUntilCall);
    }
  }

  return result;
};

// ---------------------------------------------------------------------------
// Public utilities (for tests and window debug object)
// ---------------------------------------------------------------------------

/** Clear the response cache and cancel all tracked in-flight requests. */
export const clearHearSpaceCache = (): number => {
  const count = clearCache();
  hearSpaceRequestManager.clear();
  logHearSpace(`Cache cleared (${count} entries removed, in-flight requests cancelled)`);
  return count;
};

/** Mark initial calculation as done — lets tests isolate shouldCallHearSpace logic. */
export const markInitialCalculationDone = (): void => {
  initialCalculationDone = true;
};

/**
 * Full reset: metrics + middleware debounce state + cache.
 * Supersedes the old resetHearSpaceMetrics which also cleared timers.
 */
export const resetHearSpaceMetrics = (): void => {
  resetMetrics();
  initialCalculationDone = false;
  firstTriggerTime = null;
  if (hearSpaceTimer) {
    clearTimeout(hearSpaceTimer);
    hearSpaceTimer = null;
  }
  clearHearSpaceCache();
};

// ---------------------------------------------------------------------------
// Browser debug helpers
// ---------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  (window as any).hearSpaceMetrics = {
    get: () => getHearSpaceMetrics(),
    reset: resetHearSpaceMetrics,
    clearCache: clearHearSpaceCache,
  };
}

export default hearSpaceMiddleware;
